import { translate } from 'react-i18next';
import Formsy, { addValidationRule } from 'formsy-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import getCountries from '../../common/getCountries';
import * as UserModel from '../../models/UserModel';
import getAddressConfig from './Data/AddressConfig';
import marketingRegion1Countries from './Data/marketing-region-1-countries.json';
import companySizeOptions from './Data/company-size-options.json';
import roleOptions from './Data/role-options.json';
import rolesWithExtraFields from './Data/roles-with-extra-fields.json';
import Checkbox from './elements/Checkbox';
import ValidationCheckbox from './elements/ValidationCheckbox';
import ValidationInput from './elements/ValidationInput';
import ValidationSelect from './elements/ValidationSelect';

addValidationRule('isNotDefaultSelection', (values, value) => value !== '0');

export const formTestId = 'AdditionalInfoForm-form';
export const headingTestId = 'AdditionalInfoForm-heading';
export const submitButtonTestId = 'AdditionalInfoForm-submitButton';

export class AdditionalInfoForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      config: getAddressConfig(this.props.i18n.language),
      region1CountrySelected: false,
      roleWithExtraFieldsSelected: false,
      submitDisabled: false,
    };
    this.setStateFromLanguage = this.setStateFromLanguage.bind(this);
  }

  componentDidMount() {
    this.props.i18n.on('languageChanged', this.setStateFromLanguage);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isFetching && !this.props.isFetching) {
      this.setState({ submitDisabled: false });
    }
  }

  componentWillUnmount() {
    this.props.i18n.off('languageChanged', this.setStateFromLanguage);
  }

  setStateFromLanguage(language) {
    this.setState({ config: getAddressConfig(language) });
  }

  onValidSubmit = (formData) => {
    this.setState({ submitDisabled: true });
    this.props.onSubmit(formData);
  };

  onInvalidSubmit = () => {
    this.setState({ submitDisabled: false });
  };

  // ValidationSelect component returns `event.value` from onChange, rather than `event`.
  onRoleChange = (eventValue) => {
    const selectedRoleId = eventValue;
    const isRoleWithExtraFields = selectedRoleId !== '' && rolesWithExtraFields[selectedRoleId] === true;
    this.setState({ roleWithExtraFieldsSelected: isRoleWithExtraFields });
  };

  // ValidationSelect component returns `event.value` from onChange, rather than `event`.
  onCountryChange = (eventValue) => {
    const selectedCountryIso = eventValue;
    // If a country is Region 1, users are opted out of marketing communications by default.
    // If a country is not Region 1, users are opted in to marketing communications by default.
    const countryIsRegion1 = selectedCountryIso !== '' && marketingRegion1Countries[selectedCountryIso] === true;
    this.setState({ region1CountrySelected: countryIsRegion1 });
  };

  mapInputs = inputs => UserModel.mapInputsFromAdditionalInfoEdit(this.props.userModel, inputs);

  render() {
    const { t, userModel } = this.props;
    const {
      config,
      region1CountrySelected,
      roleWithExtraFieldsSelected,
      submitDisabled,
    } = this.state;

    return (
      <div id="form" className="AdditionalInfoForm clearfix">
        <h1 className="title heading--h2" data-test-id={headingTestId}>
          {t('Tableau_Public_needs_more_information', { defaultValue: 'Tableau Public needs more information' })}
        </h1>
        <Formsy
          className="form--full-width"
          data-test-id={formTestId}
          onValidSubmit={this.onValidSubmit}
          onInvalidSubmit={this.onInvalidSubmit}
          mapping={this.mapInputs}
          noValidate
        >
          <div className="clearfix">
            <div className="grid--4">
              <ValidationSelect
                autoFocus
                id="role"
                name="role"
                options={roleOptions}
                onChange={this.onRoleChange}
                required="isEmptyString"
                title={t('Role', { defaultValue: 'Role' })}
                validationErrors={{
                  isEmptyString: t('You_must_select_a_role.', { defaultValue: 'You must select a role.' }),
                }}
                value=""
              />
            </div>
          </div>
          <div className={`clearfix ${!roleWithExtraFieldsSelected && 'hide'}`}>
            <div className="grid--4">
              <ValidationInput
                id="company"
                maxLength="255"
                name="company"
                required={roleWithExtraFieldsSelected}
                title={t('Company', { defaultValue: 'Company' })}
                type="text"
                validationErrors={{
                  isDefaultRequiredValue: t('This_field_is_required.', { defaultValue: 'This field is required.' }),
                }}
                value={(userModel && userModel.company) || ''}
              />
            </div>
          </div>
          <div className={`clearfix ${!roleWithExtraFieldsSelected && 'hide'}`}>
            <div className="grid--4">
              <ValidationInput
                id="title"
                maxLength="255"
                name="title"
                required={roleWithExtraFieldsSelected}
                title={t('Title', { defaultValue: 'Title' })}
                type="text"
                validationErrors={{
                  isDefaultRequiredValue: t('This_field_is_required.', { defaultValue: 'This field is required.' }),
                }}
                value={(userModel && userModel.title) || ''}
              />
            </div>
          </div>
          <div className={`clearfix ${!roleWithExtraFieldsSelected && 'hide'}`}>
            <div className="grid--4">
              <ValidationSelect
                id="companySize"
                name="companySize"
                options={companySizeOptions}
                title={t('Number_of_Employees', { defaultValue: 'Number of Employees' })}
                validations={roleWithExtraFieldsSelected ? 'isNotDefaultSelection' : undefined}
                validationErrors={{
                  isNotDefaultSelection: t('This_field_is_required.', { defaultValue: 'This field is required.' }),
                }}
                value="0"
              />
            </div>
          </div>
          <div className="clearfix">
            <div className="grid--4">
              <ValidationSelect
                id="country"
                name="country"
                options={getCountries(config)}
                onChange={this.onCountryChange}
                required="isEmptyString"
                title={config.label}
                translateContents={false}
                validationErrors={{
                  isEmptyString: t('You_must_select_a_country_or_region.', { defaultValue: 'You must select a country or region.' }),
                }}
                value={(userModel && userModel.country) || ''}
              />
            </div>
          </div>
          <div className="clearfix">
            <div className="grid--6">
              <ValidationCheckbox
                id="codeOfConductAcceptance"
                name="codeOfConductAcceptance"
                required
                title={(
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t(
                        'I_agree_to_the_Data_Policy_and_Community_Code_By_registering_you_confirm_that_you_agree_to_the_processing_of_your_personal_data_as_described_in_the_Salesforce_Privacy_Statement.',
                        { defaultValue: 'I agree to the Data Policy and Community Code. By registering, you confirm that you agree to the processing of your personal data as described in the Salesforce Privacy Statement.' },
                      ),
                    }}
                  />
                )}
                validations="isTrue"
                validationError={t('You_must_agree_before_you_can_continue.', { defaultValue: 'You must agree before you can continue.' })}
              />
            </div>
          </div>
          <div className={`clearfix ${!region1CountrySelected && 'hide'}`}>
            <div className="grid--6">
              <Checkbox
                checked={!region1CountrySelected}
                id="marketingEmailOptIn"
                label={t(
                  'Yes_I_would_like_to_receive_marketing_communications_regarding_Salesforce_products_services_and_events._I_can_unsubscribe_at_any_time.',
                  { defaultValue: 'Yes, I would like to receive marketing communications regarding Salesforce products, services, and events. I can unsubscribe at any time.' },
                )}
                name="marketingEmailOptIn"
                value="true"
              />
            </div>
          </div>
          <div className="form-buttons">
            <button
              className="cta cta--secondary"
              data-test-id={submitButtonTestId}
              disabled={submitDisabled}
              id="submitAdditionalInfoButton"
              type="submit"
            >
              {t('Complete_Sign-In', { defaultValue: 'Complete Sign-In' })}
            </button>
          </div>
        </Formsy>
      </div>
    );
  }
}

AdditionalInfoForm.propTypes = {
  i18n: PropTypes.object.isRequired,
  isFetching: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  userModel: PropTypes.object.isRequired,
};

export default translate()(AdditionalInfoForm);
